import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatISO } from "date-fns";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Summary } from "store/reducers";
import { useGetConfigQuery } from "store/reducers/config/configAPI";
import { useFetchOrganisationsQuery } from "store/reducers/organisation";

import { AllowanceSummary } from "../components/AllowanceSummary";
import { Button } from "../components/button";
import { PublishProgressDisplay } from "../components/PublishProgressDisplay";
import { OrdinaryHoursSummary, WeekendHoursSummary } from "../components/WorkedHoursSummary";
import { useHandleResponseError } from "../helpers/useHandleResponseError";
import { usePublishShiftsWithProgress } from "../hooks/usePublishShiftsWithProgress";
import { Duration } from "../timesheet_viewer/table_components/duration";
import { makeLocationPreserveParams } from "../timesheet_viewer/useStartDate";

export type SummaryData = {
  employee: Employee;
  summary: Summary;
  leave_total: number;
}[];

type Props = {
  startDate: Date | null;
  endDate: Date | null;
  organisation: Organisation;
};

export function SummaryTable(props: Props) {
  const { startDate, endDate, organisation } = props;
  const location = useLocation();
  const { data: orgStore } = useFetchOrganisationsQuery();
  const { executePublishShifts, progressList } = usePublishShiftsWithProgress({});
  const { data: configData } = useGetConfigQuery();
  const [summaryData, setSummaryData] = React.useState<SummaryData>();
  const handleResponseError = useHandleResponseError();

  // Track which employee's publish operation is showing progress
  const [publishingEmployeeId, setPublishingEmployeeId] = React.useState<number>();

  React.useEffect(() => {
    async function fetchSummary() {
      if (!startDate) {
        return;
      }
      let response = await fetch("/api/summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          start_date: formatISO(startDate, { representation: "date" }),
          xero_tenantId: organisation.xero_tenantId,
        }),
      });
      if (response.ok) {
        setSummaryData(await response.json());
      } else {
        handleResponseError(response);
      }
    }
    fetchSummary();
  }, [startDate, organisation, handleResponseError]);

  return (
    <table className="table">
      <thead>
        <tr>
          <th rowSpan={1} colSpan={5} className="table-header-name">
            <FontAwesomeIcon icon="info" /> Employee Summary
          </th>
        </tr>
        <tr>
          <th className="table-header-column-name">Employee</th>
          <th className="table-header-column-name">Ordinary</th>
          <th className="table-header-column-name">Weekend</th>
          <th className="table-header-column-name">Allowances</th>
          <th className="table-header-column-name">Leave / TOIL</th>
        </tr>
      </thead>
      <tbody>
        {summaryData?.map((e, i) => {
          let organisation = orgStore?.organisations.find((o) => o.xero_tenantId === e.employee.xero_tenantId);

          let employeeTSLocation = makeLocationPreserveParams(location, {
            pathname: "/timesheets",
            employee_id: e.employee.id,
          });

          return (
            <React.Fragment key={i}>
              <tr>
                <td className="table-body-cell">
                  <div className="flex items-center gap-2">
                    <Link className="min-w-0 flex-1 break-words" to={employeeTSLocation}>
                      {e.employee.name}
                    </Link>

                    {configData?.debug && (
                      <Button
                        className="flex-shrink-0"
                        colour="accent"
                        size="sm"
                        onClick={() => {
                          if (organisation && startDate && endDate) {
                            setPublishingEmployeeId(e.employee.id);
                            executePublishShifts({
                              organisation,
                              startDate,
                              endDate,
                              employee_ids: [e.employee.id],
                            });
                          }
                        }}
                      >
                        Publish
                      </Button>
                    )}
                  </div>
                </td>
                <td className="table-body-cell">
                  <OrdinaryHoursSummary summary={e.summary} />
                </td>
                <td className="table-body-cell">
                  <WeekendHoursSummary summary={e.summary} />
                </td>
                <td className="table-body-cell">
                  <AllowanceSummary summary={e.summary} />
                </td>
                <td className="table-body-cell">
                  {e.leave_total > 0 && (
                    <div>
                      Leave: <Duration seconds={e.leave_total} />
                    </div>
                  )}
                  {e.summary.toil_accrual > 0 && (
                    <div>
                      TOIL accrual: <Duration seconds={e.summary.toil_accrual} />
                    </div>
                  )}
                </td>
              </tr>

              {/* Show progress display for the specific employee being published */}
              {publishingEmployeeId === e.employee.id && progressList && (
                <tr>
                  <td colSpan={5} className="table-body-cell p-0 text-left">
                    <PublishProgressDisplay progressList={progressList} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
